import { Block, Check } from "@mui/icons-material";
import { Box, Button, Card, CardActionArea, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { sl } from 'date-fns/locale/sl';
import I18n from 'i18next';
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../common/components/CustomDialog";
import CustomLoader from "../../common/components/CustomLoader";
import CustomLoadMore from "../../common/components/CustomLoadMore";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { useAuth } from "../../common/hooks/useAuth";
import { useLoading } from "../../common/hooks/useLoading";
import { useNotifications } from "../../common/hooks/useNotifications";
import { IBeehiveAlert } from "../../common/interfaces/beehiveAlerts";
import { ICustomDialogProps } from "../../common/interfaces/dialog";
import { ICustomOption } from "../../common/interfaces/form";
import beehiveAlertsService from "../../common/services/beehiveAlertsService";
import beehiveService from "../../common/services/beehiveService";

const itemsPerPage = 48;

interface INotificationsProps { }

const Notifications: FC<INotificationsProps> = () => {
    // global
    const { notifications, setNotifications, currentNotification, setCurrentNotification, setHeaderNotifications } = useNotifications();
    const { loading } = useLoading();
    const { permissionsState } = useAuth();
    const { t } = useTranslation();
    const locale = I18n.language;
    const [language, setLanguage] = useState(locale);
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [beehives, setBeehives] = useState<ICustomOption[]>([]);
    const [currentAlert, setCurrentAlert] = useState<IBeehiveAlert | undefined>(undefined);
    const [unreadAlertsList, setUnreadAlertsList] = useState<IBeehiveAlert[]>([]);
    const [alertsByMonth, setAlertsByMonth] = useState<{ [x: string]: IBeehiveAlert[] } | undefined>(undefined);
    const [selectedBeehive, setSelectedBeehive] = useState<ICustomOption | undefined>(undefined);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    // load more controls
    const [count, setCount] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);

    const getAlertsList = useCallback(async (alertsList: IBeehiveAlert[], count: number, beehiveId?: string) => {
        try {
            const response = beehiveId ?
                await beehiveAlertsService.getAllForBeehive(beehiveId, (count - 1) * itemsPerPage, itemsPerPage) :
                await beehiveAlertsService.getAllGeneric((count - 1) * itemsPerPage, itemsPerPage);
            const completeList = [...alertsList, ...response.content];
            setTotalItems(response.pageInfo.totalItems);
            setNotifications([...completeList]);
            setHeaderNotifications([...completeList.filter(alert => !alert.isRead).slice(0, 5)])
            setUnreadAlertsList([...completeList.filter(alert => !alert.isRead)]);
            if (completeList.length > 0) {
                // convert our final result back into key/values pairs
                const alerts = Object.fromEntries(
                    Object.entries(
                        completeList.reduce((groupedByMonth: { [x: string]: IBeehiveAlert[] }, alert) => {
                            let month = format(alert.timestamp, "LLLL yyyy", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB }); // set up month and year
                            if (groupedByMonth.hasOwnProperty(month))
                                groupedByMonth[month].push(alert);
                            else
                                groupedByMonth[month] = [alert];  // create or add to accumulating month in the object
                            return groupedByMonth;
                        }, {})
                    )
                )
                setAlertsByMonth(alerts);
            }
        } catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        } finally {
            setIsLoading(false);
        }
    }, [])

    const getBeehivesList = async () => {
        try {
            const response = await beehiveService.getAll();
            setBeehives(response.content.map(beehive => { return { value: beehive.id, desc: beehive.name } }));
        } catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        if (alertsByMonth) {
            let alerts: { [x: string]: IBeehiveAlert[] } = {}
            let oldLabels = Object.keys(alertsByMonth)
            let labels = Object.keys(alertsByMonth).map(
                label => format(alertsByMonth[label][0].timestamp, "LLLL yyyy", {
                    locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB
                })
            )
            labels.forEach((l, i) => {
                Object.assign(alerts, {
                    ...alerts,
                    [l]: alertsByMonth[oldLabels[i]]
                })
            })
            setAlertsByMonth(alerts)
        }
    }, [language])

    useEffect(() => {
        if (locale !== language) {
            setLanguage(locale)
        }
    }, [locale, language])

    useEffect(() => {
        if (currentNotification) {
            setOpenDialog(true)
            onNotificationClick(currentNotification)
            getAlertsList([...notifications], count);
        }
    }, [currentNotification])

    useEffect(() => {
        setIsLoading(true);
        if (selectedBeehive) {
            getAlertsList([...notifications], count, selectedBeehive.value.toString());
        } else {
            getAlertsList([...notifications], count);
            getBeehivesList();
        }
    }, [count, selectedBeehive, getAlertsList])

    const onSelectChange = (e: SelectChangeEvent) => {
        setCount(1);
        setNotifications([]);
        setHeaderNotifications([])
        setUnreadAlertsList([]);
        setAlertsByMonth(undefined);
        setSelectedBeehive(beehives.find(beehive => beehive.value === e.target.value))
    }

    const onNotificationClick = async (data: IBeehiveAlert | IBeehiveAlert[]) => {
        setIsLoading(true);
        try {
            if (Array.isArray(data)) {
                await beehiveAlertsService.post({ alertIds: data.map(alert => alert.id) })
                setOpenSnackbar(true);
                setResponseType("success");
                setMessageSnackbar(t("genericSuccess"));
                setUnreadAlertsList([]);
                setNotifications([...notifications.map(alert => { return { ...alert, isRead: true } })])
                setHeaderNotifications([])
                if (alertsByMonth) {
                    let alerts: { [x: string]: IBeehiveAlert[] } = {}
                    let labels = Object.keys(alertsByMonth)
                    labels.forEach((l, i) => {
                        Object.assign(alerts, {
                            ...alerts,
                            [l]: alertsByMonth[labels[i]].map(alert => { return { ...alert, isRead: true } })
                        })
                    })
                    setAlertsByMonth(alerts)
                }
            } else {
                if (!data.isRead) {
                    setCurrentAlert(data);
                    setOpenDialog(true);
                    setCurrentNotification(undefined);
                    await beehiveAlertsService.post({ alertIds: [data.id] });
                    const newAllAlerts = [...notifications];
                    const findInAllAlerts = notifications.find(alert => alert.id === data.id)
                    const newAlertsByMonth = { ...alertsByMonth };
                    if (newAlertsByMonth) {
                        let alerts: { [x: string]: IBeehiveAlert[] } = {}
                        let labels = Object.keys(newAlertsByMonth)
                        labels.forEach((l, i) => {
                            const findInAlertsByMonth = newAlertsByMonth[labels[i]].find(alert => alert.id === data.id)
                            if (findInAlertsByMonth)
                                newAlertsByMonth[labels[i]].splice(newAlertsByMonth[labels[i]].indexOf(findInAlertsByMonth), 1, { ...findInAlertsByMonth, isRead: true })
                            Object.assign(alerts, {
                                ...alerts,
                                [l]: newAlertsByMonth[labels[i]]
                            })
                        })
                        setAlertsByMonth(alerts)
                    }
                    if (findInAllAlerts) {
                        newAllAlerts.splice(newAllAlerts.indexOf(findInAllAlerts), 1, { ...findInAllAlerts, isRead: true })
                        setNotifications(newAllAlerts)
                        setHeaderNotifications([...newAllAlerts.filter(alert => !alert.isRead).slice(0, 5)])
                    }
                } else {
                    setCurrentAlert(data);
                    setOpenDialog(true);
                }
            }
        } catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message || "Please try again later")
            } else {
                setMessageSnackbar(t("genericError"));
            }
        } finally {
            setIsLoading(false);
        }
    }

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: "info",
        close: () => setOpenDialog(false),
        title: currentAlert ? (locale === "it-IT" ? currentAlert.content_IT.title : currentAlert.content_EN.title) : "",
        open: openDialog,
        fullWidth: true
    };

    return <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <CustomDialog {...dialogProps}>
            {
                currentAlert && <>
                    <Typography variant="body2" sx={{ mb: 1 }} >
                        {
                            format(currentAlert.timestamp, locale === "en-EN" ? "PPPP, hh:mm aa" : "PPPP, HH:mm", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })
                        }
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {
                            locale === "it-IT" ? currentAlert.content_IT.description : currentAlert.content_EN.description
                        }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: currentAlert.isRead ? 400 : 600, }}>
                        {currentAlert.house?.name}
                    </Typography>
                </>
            }
        </CustomDialog>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
                {t("notificationsPage.title")}
            </Typography>
            {
                permissionsState &&
                permissionsState.existEditAlertsPermission &&
                <Button variant="contained"
                    sx={(theme) => ({
                        [theme.breakpoints.only("xs")]: {
                            minWidth: { xs: 0 },
                            width: { xs: 34 },
                            height: { xs: 32 },
                            padding: { xs: 2 }
                        }
                    })}
                    disabled={unreadAlertsList.length === 0}
                    onClick={() => onNotificationClick(unreadAlertsList)}
                >
                    <Check sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                    {largeMediumScreen && t("notificationsPage.markAllAsReadButtonLabel")}
                </Button>
            }
        </Box>
        {
            permissionsState &&
            permissionsState.existViewBeehivesPermission &&
            <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                <InputLabel id="period-select-label">
                    {t("notificationsPage.selectBeehivePlaceholder")}
                </InputLabel>
                <Select
                    id="beehive-select"
                    value={selectedBeehive?.value.toString() || ""}
                    name="beehive"
                    onChange={onSelectChange}
                    labelId="beehive-select-label"
                    label={t("notificationsPage.selectBeehivePlaceholder")}
                    MenuProps={{
                        style: {
                            maxHeight: 400,
                        },
                    }}
                >
                    <MenuItem value="">
                        <em>{t("notificationsPage.genericSelectLabel")}</em>
                    </MenuItem>
                    {beehives &&
                        beehives.map((option) => (
                            <MenuItem
                                key={option.value + "-" + option.desc}
                                value={option.value}
                            >
                                {option.desc}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
        }
        {
            loading && count === 1 ? <CustomLoader /> : <Box>
                {
                    !alertsByMonth ? <Typography sx={{ my: 2, fontWeight: 600, display: 'flex', alignItems: "center" }}>
                        <Block sx={{ mr: 1 }} />
                        {t("notificationsPage.noDataLabel")}
                    </Typography> : <Box>
                        {
                            Object.keys(alertsByMonth).map((label, i) =>
                                <Box key={label + "-" + i}>
                                    <Typography variant="h6" sx={{ my: 2 }}>
                                        {label}
                                    </Typography>
                                    <Grid container rowSpacing={2} columnSpacing={2}>
                                        {
                                            alertsByMonth[label].map((alert, index) =>
                                                <Grid key={alert.id + "-" + index} item xs={12} sm={6} md={4} lg={3} xl={3}>
                                                    <Card>
                                                        <CardActionArea onClick={() => {
                                                            if (permissionsState && permissionsState.existEditAlertsPermission)
                                                                onNotificationClick(alert)
                                                        }}>
                                                            <CardContent>
                                                                <Typography gutterBottom variant="body1" component="div" sx={{
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    fontWeight: alert.isRead ? 400 : 600
                                                                }}>
                                                                    {
                                                                        locale === "it-IT" ? alert.content_IT.title : alert.content_EN.title
                                                                    }
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                    {format(alert.timestamp, locale === "en-EN" ? "PPPP, hh:mm aa" : "PPPP, HH:mm", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{
                                                                    fontWeight: alert.isRead ? 400 : 600,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}>
                                                                    {alert.house?.name}
                                                                </Typography>
                                                            </CardContent>
                                                        </CardActionArea>
                                                    </Card>
                                                </Grid>
                                            )
                                        }
                                    </Grid>
                                </Box>
                            )
                        }
                        {
                            Math.round(totalItems / itemsPerPage) >= count &&
                            totalItems > itemsPerPage &&
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <CustomLoadMore count={count} setCount={setCount} loading={isLoading} />
                            </Box>
                        }
                    </Box>
                }
            </Box>
        }
    </Box >
}

export default Notifications;